import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import WhyShopWithUs from "../component/WhyShopWithUs"
import HeroImg from "../assets/Ucart.jpg";
import { auth,db } from '../FirebaseConfig';
import { motion, AnimatePresence } from 'framer-motion';
import About from '../component/About';
import SearchAndFilter from '../component/SearchAndFilter';
import ProductGrid from '../component/ProductGrid';
import CategoryCards from '../component/CategoryCards';

// Inline Firebase setup to resolve compilation issues
import { initializeApp } from "firebase/app";
import { getFirestore, collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";



// ────────────────────── LIVE ACTIVITY TICKER ──────────────────────

const ACTIVITY_FEED = [
  { name: 'Chisom A.', action: 'just bought a HP Laptop', time: '2m ago', avatar: 'C' },
  { name: 'Tunde B.', action: 'listed Airpods Pro for ₦28,000', time: '5m ago', avatar: 'T' },
  { name: 'Blessing O.', action: 'just bought Ankara fabric', time: '8m ago', avatar: 'B' },
  { name: 'Emeka N.', action: 'listed iPhone 13 for ₦190,000', time: '11m ago', avatar: 'E' },
  { name: 'Ada C.', action: 'just bought study materials', time: '14m ago', avatar: 'A' },
  { name: 'Kene U.', action: 'listed a room fan for ₦12,500', time: '17m ago', avatar: 'K' },
];

const LiveActivityTicker = () => {
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const colors = ['#16a34a', '#15803d', '#22c55e', '#4ade80', '#86efac', '#bbf7d0'];

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        // Fetch latest 10 approved products
        const q = query(
          collection(db, 'Products'),
          where('approved', '==', true),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        const snap = await getDocs(q);
        const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        if (fetched.length === 0) return;

        const feed = fetched.map((p, i) => {
          const rawName = p.storeName || p.sellerName || 'A seller';
          const rawPrice = p.price
            ? `₦${Number(String(p.price).replace(/,/g, '')).toLocaleString()}`
            : '';
          const rawProduct = p.name || 'a product';

          // Truncate helpers
          const storeName = rawName.length > 14 ? rawName.slice(0, 13) + '…' : rawName;
          const productName = rawProduct.length > 18 ? rawProduct.slice(0, 17) + '…' : rawProduct;
          const price = rawPrice.length > 10 ? rawPrice.slice(0, 9) + '…' : rawPrice;

          // Time label: real products get a relative "ago" feel
          const seconds = p.createdAt?.seconds;
          let timeLabel = 'recently';
          if (seconds) {
            const diffMin = Math.floor((Date.now() / 1000 - seconds) / 60);
            if (diffMin < 1) timeLabel = 'just now';
            else if (diffMin < 60) timeLabel = `${diffMin}m ago`;
            else if (diffMin < 1440) timeLabel = `${Math.floor(diffMin / 60)}h ago`;
            else timeLabel = `${Math.floor(diffMin / 1440)}d ago`;
          }

          return {
            storeName,
            action: `listed ${productName}${price ? ` for ${price}` : ''}`,
            time: timeLabel,
            avatar: rawName.charAt(0).toUpperCase(),
            colorIndex: i,
          };
        });

        setItems(feed);
      } catch (err) {
        console.error('LiveActivityTicker fetch error:', err);
      }
    };

    fetchFeed();
  }, []);

  useEffect(() => {
    if (items.length === 0) return;
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex(p => (p + 1) % items.length);
        setVisible(true);
      }, 400);
    }, 3500);
    return () => clearInterval(interval);
  }, [items]);

  if (items.length === 0) return null;

  const item = items[index];

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.2 }}
      className="flex items-center gap-3 bg-black/70 border border-green-500/25 rounded-full px-4 py-2 w-fit max-w-[320px]"
    >
      <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
      </span>
      <AnimatePresence mode="wait">
        {visible && (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.3 }}
            className="flex items-center gap-2 min-w-0"
          >
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black text-white flex-shrink-0"
              style={{ backgroundColor: colors[item.colorIndex % colors.length] }}
            >
              {item.avatar}
            </div>
            <span className="text-xs text-gray-300 truncate">
              <span className="text-white font-bold">{item.storeName}</span>{' '}
              <span className="truncate">{item.action}</span>
            </span>
            <span className="text-[10px] text-gray-500 font-mono flex-shrink-0 ml-1">{item.time}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ────────────────────── REFERRAL PROMO SECTION ──────────────────────

const ReferralPromo = () => {
  // Sets the exact launch date: May 15, 2026 at 00:00:00
  const targetDate = new Date("2026-05-15T00:00:00").getTime();

  // Helper function to calculate exact time remaining
  const calculateTimeLeft = () => {
    const now = new Date().getTime();
    const difference = targetDate - now;

    if (difference <= 0) {
      return { isLive: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      isLive: false,
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  // Triggers the countdown every 1 second
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    // Cleans up the timer if the component unmounts
    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="w-full relative bg-gradient-to-br from-green-900/40 to-black border border-green-500/40 rounded-3xl p-6 md:p-8 shadow-2xl shadow-green-900/30 overflow-hidden"
    >
      {/* Decorative Glow */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/10 rounded-full blur-3xl pointer-events-none" />

      {timeLeft.isLive ? (
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20 border border-green-500/50 text-green-400 font-mono text-xs font-bold uppercase tracking-widest mb-3">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
              Live Now
            </div>
            <h3 className="text-2xl md:text-4xl font-black text-white mb-2">
              The Referral Giveaway is Here! 🎉
            </h3>
            <p className="text-gray-300 text-sm md:text-base">
              Start inviting your friends and earn instant cash today. Limited time only!
            </p>
          </div>
          <Link
            to="/JoinReferral"
            className="whitespace-nowrap px-8 py-4 rounded-xl bg-green-500 text-black font-extrabold hover:bg-green-400 transition-all duration-300 shadow-lg shadow-green-500/30 transform hover:scale-105"
          >
            Start Earning Now 💰
          </Link>
        </div>
      ) : (
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <h3 className="text-2xl md:text-3xl font-black text-white mb-2 flex items-center justify-center md:justify-start gap-2">
              🎁 Our Referral Giveaway
            </h3>
            <p className="text-gray-300 text-sm md:text-base">
              Get ready to earn cash by inviting friends. The program unlocks in:
            </p>
          </div>

          <div className="flex gap-3 sm:gap-4">
            {[
              { label: 'Days', value: timeLeft.days },
              { label: 'Hours', value: timeLeft.hours },
              { label: 'Mins', value: timeLeft.minutes },
              { label: 'Secs', value: timeLeft.seconds }
            ].map((time, idx) => (
              <div key={idx} className="flex flex-col items-center bg-black/60 border border-green-500/30 rounded-xl p-3 sm:p-4 min-w-[70px] sm:min-w-[80px] shadow-inner">
                <span className="text-2xl sm:text-3xl font-black text-green-400 font-mono">
                  {time.value.toString().padStart(2, '0')}
                </span>
                <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-widest font-bold mt-1">
                  {time.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};


// ────────────────────── STATS BAR ──────────────────────

const StatsBar = () => {
  const stats = [
    { value: '200+', label: 'Sellers', icon: '🏪' },
    { value: '600+', label: 'Users', icon: '👥' },
    { value: '300+', label: 'Products', icon: '📦' },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 1 }}
      className="mt-8 w-full"
    >
      <div className="flex gap-3 justify-center lg:justify-start flex-wrap">
        {stats.map(({ value, label, icon }, i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.1 + i * 0.1 }}
            className="flex items-center gap-2 bg-black/60 border border-green-500/30 rounded-xl px-4 py-2.5 shadow-lg shadow-green-500/10"
          >
            <span className="text-lg">{icon}</span>
            <div>
              <div className="text-lg font-black text-green-400 font-mono leading-none">{value}</div>
              <div className="text-[9px] text-gray-400 font-mono tracking-widest uppercase">{label}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

// ────────────────────── TRUST BADGE STRIP ──────────────────────

const TrustStrip = () => {
  const badges = [ 
    { icon: '🎓', text: 'Students Only' },
    { icon: '🔒', text: 'Secure Paystack Payments' },
    { icon: '✅', text: 'Verified Sellers' },
    { icon: '⚡', text: 'Same-Day Pickup' },
    { icon: '🛡️', text: 'Buyer Protection' },
    { icon: '📱', text: 'WhatsApp Support' },
  ];
  return (
    <div className="relative overflow-hidden bg-black/40 border-y border-green-500/15 py-3 mt-16 sm:mt-24">
      <motion.div
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
        className="flex gap-10 whitespace-nowrap"
      >
        {[...badges, ...badges].map((badge, i) => (
          <div key={i} className="flex items-center gap-2 text-sm text-gray-300 flex-shrink-0">
            <span>{badge.icon}</span>
            <span className="font-medium">{badge.text}</span>
            <span className="text-green-500/40 ml-4">◆</span>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

// ────────────────────── HOW IT WORKS ──────────────────────

const HowItWorks = () => {
  const [activeTab, setActiveTab] = useState('buy');
  const buyerSteps = [
    { step: '01', icon: '🔍', title: 'Browse Products', desc: 'Search hundreds of verified campus listings in seconds.' },
    { step: '02', icon: '💳', title: 'Pay Securely', desc: 'Pay once via Paystack. Your money is protected until pickup.' },
    { step: '03', icon: '📦', title: 'Pick Up & Done', desc: 'WhatsApp the seller, arrange pickup, collect your item.' },
  ];
  const sellerSteps = [
    { step: '01', icon: '📸', title: 'List for Free', desc: 'Take a photo and list your item in under 2 minutes.' },
    { step: '02', icon: '🔔', title: 'Get Notified', desc: 'We alert you instantly when a buyer places an order.' },
    { step: '03', icon: '💰', title: 'Get Paid', desc: 'Hand over the item at pickup. Payment hits your account.' },
  ];

  return (
    <section className="relative px-4 md:px-12 py-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-green-950/5 to-transparent pointer-events-none" />
      <div className="max-w-5xl mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 font-mono text-xs tracking-widest uppercase mb-4">
            Simple by design
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-white">
            How uCart <span className="text-green-400">Works</span>
          </h2>
          <p className="text-gray-400 mt-3 text-base max-w-lg mx-auto">
            Built for busy students. No drama, no middleman rubbish.
          </p>
        </motion.div>

        {/* Toggle */}
        <div className="flex justify-center mb-10">
          <div className="flex bg-white/5 border border-white/10 rounded-full p-1 gap-1">
            {['buy', 'sell'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
                  activeTab === tab ? 'bg-green-500 text-black shadow-lg shadow-green-500/30' : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab === 'buy' ? '🛒 I Want to Buy' : '💼 I Want to Sell'}
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35 }}
            className="grid md:grid-cols-3 gap-6"
          >
            {(activeTab === 'buy' ? buyerSteps : sellerSteps).map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex flex-col items-center text-center p-6 rounded-2xl bg-white/[0.03] border border-white/[0.07] hover:border-green-500/30 hover:bg-green-500/5 transition-all duration-300 group"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">{s.icon}</div>
                <div className="text-green-400 font-mono text-xs tracking-widest mb-2">{s.step}</div>
                <h3 className="text-white font-black text-lg mb-2">{s.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-center mt-10">
          <Link
            to={activeTab === 'buy' ? '/product' : '/Profile'}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-green-500 text-black font-extrabold text-sm hover:bg-green-400 transition-all duration-300 shadow-lg shadow-green-500/30 hover:scale-[1.03]"
          >
            {activeTab === 'buy' ? '🛒 Start Shopping Now' : '💼 List Your First Item Free'}
          </Link>
        </div>
      </div>
    </section>
  );
};

// ────────────────────── TESTIMONIALS ──────────────────────

const TESTIMONIALS = [
  { name: 'Chisom Eze', faculty: 'Engineering, 300L', avatar: 'C', color: '#16a34a', stars: 5, text: 'Bought a laptop for ₦280k. Pickup was smooth. 10/10 experience' },
  { name: 'Tunde Balogun', faculty: 'Law, 200L', avatar: 'T', color: '#22c55e', stars: 5, text: 'Sold notebooks within 24 hours of listing. Got paid' },
  { name: 'Blessing Okafor', faculty: 'Medicine, 400L', avatar: 'B', color: '#4ade80', stars: 5, text: "Finally a campus marketplace that isn't sketchy. Paystack payment means my money is safe before pickup." },
  { name: 'Adaeze Nwosu', faculty: 'Sciences, 100L', avatar: 'A', color: '#86efac', stars: 5, text: 'Very easy to sell. Hoping for sales' },
];

const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCurrent(p => (p + 1) % TESTIMONIALS.length), 4500);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative px-4 md:px-12 py-20 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-green-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="max-w-4xl mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 font-mono text-xs tracking-widest uppercase mb-4">
            Real students. Real deals.
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-white">
            What student says About <span className="text-green-400">uCart</span>
          </h2>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, scale: 0.97, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: -10 }}
            transition={{ duration: 0.4 }}
            className="bg-white/[0.04] border border-white/10 rounded-3xl p-8 md:p-10 mb-6 relative overflow-hidden"
          >
            <div className="absolute top-6 right-8 text-green-500/10 text-9xl font-black leading-none select-none pointer-events-none">"</div>
            <div className="flex gap-1 mb-4">
              {Array(TESTIMONIALS[current].stars).fill(0).map((_, i) => (
                <span key={i} className="text-yellow-400 text-lg">★</span>
              ))}
            </div>
            <p className="text-white text-lg md:text-xl leading-relaxed font-medium mb-6 relative z-10">
              "{TESTIMONIALS[current].text}"
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center font-black text-white text-sm"
                style={{ backgroundColor: TESTIMONIALS[current].color }}>
                {TESTIMONIALS[current].avatar}
              </div>
              <div>
                <p className="text-white font-bold text-sm">{TESTIMONIALS[current].name}</p>
                <p className="text-gray-500 text-xs font-mono">{TESTIMONIALS[current].faculty} · UNN</p>
              </div>
              <div className="ml-auto bg-green-500/10 border border-green-500/20 rounded-full px-3 py-1">
                <span className="text-green-400 text-xs font-mono">Verified Buyer</span>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-center gap-2">
          {TESTIMONIALS.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`rounded-full transition-all duration-300 ${i === current ? 'w-6 h-2 bg-green-400' : 'w-2 h-2 bg-white/20 hover:bg-white/40'}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// ────────────────────── STICKY CTA ──────────────────────

const StickyCTA = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const fn = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          className="fixed bottom-6 left-0 right-0 flex justify-center z-50 px-4 pointer-events-none"
        >
          <div className="flex items-center gap-3 bg-black/90 backdrop-blur-xl border border-green-500/40 rounded-full px-5 py-3 shadow-2xl shadow-green-900/50 pointer-events-auto">
            <span className="relative flex h-2 w-2 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
            </span>
            <span className="text-gray-300 text-sm font-medium hidden sm:block">300+ products listed</span>
            <Link to="/product" className="px-5 py-2 rounded-full bg-green-500 text-black font-extrabold text-sm hover:bg-green-400 transition-all duration-200 hover:scale-[1.03] shadow-lg shadow-green-500/30">
              Shop Now →
            </Link>
            <Link to="/Profile" className="px-5 py-2 rounded-full border border-green-500/50 text-green-400 font-bold text-sm hover:bg-green-500/10 transition-all duration-200">
              Sell
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ────────────────────── DUAL VALUE PROP ──────────────────────

const DualValueProp = () => (
  <section className="px-4 md:px-12 py-16">
    <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
      <motion.div
        initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
        className="relative rounded-3xl overflow-hidden border border-green-500/25 bg-gradient-to-br from-green-950/40 to-black/60 p-8 group hover:border-green-500/50 transition-all duration-300"
      >
        <div className="text-5xl mb-4">🛒</div>
        <h3 className="text-2xl font-black text-white mb-2">For Buyers</h3>
        <p className="text-gray-400 text-sm leading-relaxed mb-6">Find exactly what you need — from laptops to lecture notes — at prices that make sense for a student budget.</p>
        <ul className="space-y-2 mb-8">
          {['Verified campus sellers only', 'Pay once, pick up at your convenience', 'Secure Paystack payment', 'WhatsApp seller directly after purchase'].map(t => (
            <li key={t} className="flex items-center gap-2 text-sm text-gray-300">
              <span className="text-green-400 font-bold text-xs">✓</span> {t}
            </li>
          ))}
        </ul>
        <Link to="/product" className="inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-green-500 text-black font-extrabold text-sm hover:bg-green-400 transition-all duration-300 shadow-lg shadow-green-500/20 hover:scale-[1.02]">
          Browse Products →
        </Link>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
        className="relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.04] to-black/60 p-8 group hover:border-green-500/30 transition-all duration-300"
      >
        <div className="text-5xl mb-4">💼</div>
        <h3 className="text-2xl font-black text-white mb-2">For Sellers</h3>
        <p className="text-gray-400 text-sm leading-relaxed mb-6">Turn your unused stuff into cash. Listing takes 2 minutes and your first sale could happen today.</p>
        <ul className="space-y-2 mb-8">
          {['Free to list, no monthly fees', '600+ active buyers on campus', 'Get paid before handing over item', 'We handle notifications & emails'].map(t => (
            <li key={t} className="flex items-center gap-2 text-sm text-gray-300">
              <span className="text-green-400 font-bold text-xs">✓</span> {t}
            </li>
          ))}
        </ul>
        <Link to="/Profile" className="inline-flex items-center gap-2 px-7 py-3 rounded-xl border-2 border-green-500 text-white font-extrabold text-sm hover:bg-green-500/20 transition-all duration-300 hover:scale-[1.02]">
          Start Selling Free →
        </Link>
      </motion.div>
    </div>
  </section>
);

// ────────────────────── SNOW ──────────────────────

const Snow = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width = canvas.width = window.innerWidth;
    let height = canvas.height = window.innerHeight;
    const flakeCount = width < 768 ? 50 : 90;
    const flakes = Array.from({ length: flakeCount }, () => ({
      x: Math.random() * width, y: Math.random() * height,
      r: Math.random() * 2 + 0.5, speed: Math.random() * 0.8 + 0.3
    }));
    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.beginPath();
      flakes.forEach(f => { ctx.moveTo(f.x, f.y); ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2); });
      ctx.fill();
      flakes.forEach(f => { f.y += f.speed; if (f.y > height) f.y = -5; });
      requestAnimationFrame(draw);
    };
    const resize = () => { width = canvas.width = window.innerWidth; height = canvas.height = window.innerHeight; };
    const id = requestAnimationFrame(draw);
    window.addEventListener('resize', resize);
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(id); };
  }, []);
  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-40 opacity-70" />;
};

// ────────────────────── THREE.JS ──────────────────────

const useAnimatedShapes = () => useMemo(() => {
  const shapes = [];
  const geometry = new THREE.TorusGeometry(5, 1, 16, 100);
  const material = new THREE.MeshPhongMaterial({ color: 0x4ade80, shininess: 150, specular: 0x4ade80, transparent: true, opacity: 0.85, emissive: 0x16a34a });
  const wireframeMaterial = new THREE.MeshBasicMaterial({ color: 0x4ade80, wireframe: true, transparent: true, opacity: 0.15 });
  for (let i = 0; i < 7; i++) {
    const group = new THREE.Group();
    group.add(new THREE.Mesh(geometry, material));
    group.add(new THREE.Mesh(geometry, wireframeMaterial));
    group.position.set((Math.random() - 0.5) * 200, (Math.random() - 0.5) * 200, (Math.random() - 0.5) * 100);
    group.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
    const s = Math.random() * 0.4 + 0.6; group.scale.set(s, s, s);
    shapes.push(group);
  }
  return shapes;
}, []);

const useThreeJS = (canvasRef) => {
  const animatedShapes = useAnimatedShapes();
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const sizesRef = useRef({ width: window.innerWidth, height: window.innerHeight });
  const onWindowResize = useCallback(() => {
    const c = cameraRef.current, r = rendererRef.current, s = sizesRef.current;
    if (c && r) { s.width = window.innerWidth; s.height = window.innerHeight; c.aspect = s.width / s.height; c.updateProjectionMatrix(); r.setSize(s.width, s.height); }
  }, []);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const sizes = sizesRef.current;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x050505);
    const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 1000);
    camera.position.z = 100; cameraRef.current = camera;
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    rendererRef.current = renderer;
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const pl1 = new THREE.PointLight(0x4ade80, 50); pl1.position.set(0, 50, 50); scene.add(pl1);
    const pl2 = new THREE.PointLight(0x4ade80, 30); pl2.position.set(50, -50, 50); scene.add(pl2);
    animatedShapes.forEach(s => scene.add(s));
    const grid = new THREE.GridHelper(200, 20, 0x101010, 0x101010); grid.position.y = -100; scene.add(grid);
    const clock = new THREE.Clock(); let id;
    const animate = () => {
      const t = clock.getElapsedTime();
      animatedShapes.forEach((s, i) => {
        s.position.x = Math.sin(t * 0.5 + i * 0.7) * 40;
        s.position.y = Math.cos(t * 0.4 + i * 0.7) * 40;
        s.position.z = Math.sin(t * 0.2 + i * 0.7) * 30;
        s.rotation.x += 0.008; s.rotation.y += 0.008;
      });
      grid.rotation.y = t * 0.02;
      renderer.render(scene, camera);
      id = requestAnimationFrame(animate);
    };
    window.addEventListener('resize', onWindowResize);
    animate();
    return () => { window.removeEventListener('resize', onWindowResize); cancelAnimationFrame(id); renderer.dispose(); };
  }, [animatedShapes, onWindowResize]);
};

// ────────────────────── HOMEPAGE ──────────────────────

const HomePage = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const canvasRef = useRef(null);
  const [currentFloatingTextIndex, setCurrentFloatingTextIndex] = useState(0);

  const floatingTextData = [
    { text: "Buy & Sell Safely On Campus", pos: "top-[15%] left-[5%]" },
    { text: "Trusted By Students", pos: "top-[25%] right-[10%]" },
    { text: "Fashion | Tech | Essentials", pos: "bottom-[10%] left-[10%]" },
    { text: "Fast Deals, No Stress", pos: "bottom-[15%] right-[5%]" },
    { text: "Built For Campus Life", pos: "top-[35%] left-[25%]" },
  ];

  useThreeJS(canvasRef);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFloatingTextIndex(prev => (prev + 1) % floatingTextData.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [floatingTextData.length]);

  return (
    <div className="relative min-h-screen font-['Space_Grotesk'] w-full overflow-x-hidden bg-[#000000]">

      <Snow />
      <StickyCTA />

      {/* Top Banner */}
      <div className="absolute top-0 left-0 w-full z-20 pointer-events-none">
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="w-full text-center py-2 font-bold text-sm md:text-base shadow-lg text-white bg-gradient-to-r from-red-700 to-green-700"
        >
          🎁 Welcome Freshers 2026 — Your campus marketplace is ready
        </motion.div>
      </div>

      <canvas ref={canvasRef} className="fixed top-0 left-0 w-full h-full z-0 opacity-70" />

      <div className="relative z-10 text-white pt-10 md:pt-12">

        {/* ── HERO ── */}
        <section className="relative min-h-[90vh] overflow-hidden flex items-center justify-center py-16 md:py-24">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] z-10" />

          <div className="relative w-full max-w-7xl mx-auto flex flex-col-reverse lg:flex-row items-center justify-between px-4 md:px-8 z-20">

            {/* Left */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="w-full lg:w-1/2 p-4 flex flex-col items-center lg:items-start justify-center text-center lg:text-left mt-10 lg:mt-0"
            >
              <div className="mb-5 flex justify-center lg:justify-start">
                <LiveActivityTicker />
              </div>

              <p className="text-green-400 text-base font-mono tracking-widest mb-3">/uCart: v2.0 — formerly iBuyUNN</p>

              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white leading-tight drop-shadow-lg">
                Campus <span className="text-green-400">Marketplace</span>.
              </h1>

              <motion.span
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="text-2xl sm:text-3xl lg:text-4xl text-yellow-400 font-bold block mt-3"
              >
                Redefined. WELCOME TO 2026
              </motion.span>

              <p className="mt-5 text-lg sm:text-xl text-gray-300 max-w-2xl drop-shadow-md">
                The <strong className="text-white">only</strong> secure marketplace built for students. Buy and sell <strong>Fashion</strong>, <strong>Tech</strong>, and <strong>Essentials</strong> — zero wahala.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4 w-full sm:w-auto justify-center lg:justify-start">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.5 }}>
                  <Link to="/product" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 rounded-lg bg-green-500 text-gray-900 font-extrabold text-lg hover:bg-green-400 transition-all duration-300 shadow-xl shadow-green-500/30 transform hover:scale-[1.02]">
                    🛒 Start Shopping
                  </Link>
                </motion.div>
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.7 }}>
                  <Link to="/Profile" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 rounded-lg border-2 border-green-500 text-white font-bold text-lg hover:bg-green-500/20 transition-all duration-300 transform hover:scale-[1.02]">
                    💼 Sell for Free
                  </Link>
                </motion.div>
              </div>

              <p className="mt-3 text-gray-500 text-xs font-mono text-center lg:text-left">
                Free to join · No hidden fees · Campus verified sellers only
              </p>

              <StatsBar />
            </motion.div>

            {/* Right */}
            <motion.div
              initial={{ opacity: 0, x: 50, rotate: 3 }}
              animate={{ opacity: 1, x: 0, rotate: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="w-full lg:w-1/2 p-4 flex justify-center items-center relative"
            >
              <div className="absolute inset-0 rounded-2xl bg-green-500/5 blur-2xl scale-110 pointer-events-none" />
              <img
                src={HeroImg}
                loading="lazy"
                alt="uCart Campus Marketplace"
                className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg h-auto rounded-xl shadow-3xl border-4 border-green-500/70 shadow-green-500/50 transition-all duration-700 hover:scale-[1.01]"
              />

            </motion.div>
          </div>

          {/* Floating text */}
          <div className="absolute inset-0 z-30 pointer-events-none hidden md:block">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentFloatingTextIndex}
                initial={{ opacity: 0, y: 10, rotate: -5 }}
                animate={{ opacity: 1, y: 0, rotate: 0 }}
                exit={{ opacity: 0, y: -10, rotate: 5 }}
                transition={{ duration: 0.5 }}
                className={`absolute text-sm lg:text-base font-bold px-4 py-2 rounded-full shadow-lg backdrop-blur-sm border text-green-300 shadow-green-500/30 border-green-600/50 ${floatingTextData[currentFloatingTextIndex].pos}`}
                style={{ backgroundColor: "rgba(0,0,0,0.75)" }}
              >
                {floatingTextData[currentFloatingTextIndex].text}
              </motion.div>
            </AnimatePresence>
          </div>
        </section>

        {/* ── REFERRAL PROMO SECTION ── */}
        <section className="relative z-20 px-4 md:px-12 max-w-5xl mx-auto mt-10 md:-mt-10">
          <ReferralPromo />
        </section>

        {/* ── TRUST STRIP ── */}
        <TrustStrip />

        {/* ── WHY SHOP WITH US ── */}
        <section className="bg-black/10 pt-16 pb-12">
          <WhyShopWithUs />
        </section>

        {/* ── DUAL VALUE PROP ── */}
        <DualValueProp />

        {/* ── HOW IT WORKS ── */}
        <HowItWorks />

        {/* ── TESTIMONIALS ── */}
        <Testimonials />

        {/* ── WHATSAPP COMMUNITY ── */}
        <section className="relative px-4 md:px-12 py-20 overflow-hidden">
          <div className="absolute -top-20 -left-20 w-72 h-72 bg-green-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-green-700/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-6xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex justify-center mb-5">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 font-mono text-xs tracking-widest uppercase">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block" />
                Live Community
              </span>
            </motion.div>
            <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center text-4xl md:text-6xl font-black text-white leading-tight mb-4">
              The uCart <span className="text-green-400">WhatsApp</span> Community
            </motion.h2>
            <p className="text-center text-gray-400 text-base md:text-lg max-w-2xl mx-auto mb-14">
              Your campus trading network — live deals, real connections, zero stress.
            </p>
            <div className="grid lg:grid-cols-5 gap-6 items-stretch">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:col-span-2 flex flex-col gap-4">
                {[
                  { icon: '⚡', title: 'Instant Deal Alerts', desc: 'Be first to know when hot products drop on campus.' },
                  { icon: '✅', title: 'Verified Members Only', desc: 'Trade confidently with students vetted by the uCart team.' },
                  { icon: '💬', title: 'Campus Tips & Talk', desc: 'Share experiences, ask questions, get answers fast.' },
                  { icon: '🎁', title: 'Exclusive Giveaways', desc: "Community-only events and promos you won't find anywhere else." },
                ].map((item, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.25 + i * 0.08 }}
                    className="flex items-start gap-4 p-4 rounded-xl bg-white/[0.03] border border-white/[0.07] hover:border-green-500/30 hover:bg-green-500/5 transition-all duration-300"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center text-lg">{item.icon}</div>
                    <div>
                      <p className="text-white font-bold text-sm">{item.title}</p>
                      <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }}
                className="lg:col-span-1 flex flex-col justify-between p-6 rounded-2xl bg-gradient-to-b from-green-900/30 to-black/40 border border-green-500/25 shadow-xl shadow-green-900/20"
              >
                <div>
                  <div className="text-4xl mb-4">🤝</div>
                  <h3 className="text-white font-black text-xl leading-tight mb-3">Join 350+ students already inside</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">The biggest campus marketplace community. Students from every faculty, every hostel.</p>
                </div>
                <div className="mt-8 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-2">
                      {['S','A','T','C'].map((l, i) => (
                        <div key={i} className="w-7 h-7 rounded-full border-2 border-black flex items-center justify-center text-[10px] font-bold text-white"
                          style={{ backgroundColor: ['#16a34a','#15803d','#22c55e','#4ade80'][i] }}>{l}</div>
                      ))}
                    </div>
                    <span className="text-gray-400 text-xs">+300 members</span>
                  </div>
                  <a href="https://chat.whatsapp.com/LmcchD8QjgBGiwHe1sNgk7?mode=ac_t" target="_blank" rel="noopener noreferrer"
                    className="w-full text-center px-6 py-3.5 rounded-xl bg-green-500 text-black font-extrabold text-sm hover:bg-green-400 transition-all duration-300 shadow-lg shadow-green-500/30 hover:scale-[1.03]"
                  >
                    Join on WhatsApp →
                  </a>
                  <p className="text-center text-gray-600 text-[10px] font-mono">Free to join · UNN students only</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── SEARCH ── */}
        <section className="px-4 md:px-12 py-12 bg-black/20">
          <SearchAndFilter onSearch={setSearch} onCategoryChange={setCategory} />
        </section>

        {/* ── CATEGORIES ── */}
        <section className="px-4 md:px-12 pt-12 pb-8 bg-black/10">
          <h2 className="text-3xl md:text-4xl font-extrabold text-green-400 mb-8 drop-shadow-neon border-b border-green-500/20 pb-3">
            Shop By Category
          </h2>
          <CategoryCards onCategoryClick={setCategory} />
        </section>

        {/* ── PRODUCT GRID ── */}
        <section className="px-4 md:px-12 pt-12 pb-16 bg-black/20">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl md:text-4xl font-extrabold text-green-400 drop-shadow-neon">
              Explore Popular Products
            </h2>
            <Link to="/product" className="text-green-400 text-sm font-bold hover:text-green-300 transition-colors hidden sm:flex items-center gap-1">
              View All →
            </Link>
          </div>
          <ProductGrid search={search} category={category} />
        </section>

        {/* ── ABOUT ── */}
        <section className="mt-16 bg-black/60 p-6 md:p-12 rounded-t-3xl mx-auto max-w-7xl shadow-3xl shadow-green-500/20 backdrop-blur-md border border-b-0 border-green-500/20">
          <About />
        </section>

      </div>
    </div>
  );
};

export default HomePage;